/**
 * Root store for Idle Depths.
 *
 * Contains all application stores and services.
 * Implements the "root store pattern" for MobX.
 */

import { AppStore } from './AppStore';
import { AuthStore } from './AuthStore';
import { BankStore } from './BankStore';
import { CharacterStore } from './CharacterStore';
import { EntitlementsStore } from './EntitlementsStore';
import { GameStore } from './GameStore';
import { LocationStore } from './LocationStore';
import { NotificationStore } from './NotificationStore';
import { PreferencesStore } from './PreferencesStore';
import { ShopStore } from './ShopStore';
import { SkillsStore } from './SkillsStore';
import { UIStore } from './UIStore';

import { LocalStorageService } from '@/services/LocalStorageService';
import { SyncService } from '@/services/SyncService';

/**
 * Root store that contains all application stores.
 *
 * This implements the "root store pattern" for MobX, allowing:
 * - Easy access to all stores from any component
 * - Cross-store communication
 * - Centralized store initialization
 *
 * @example
 * ```tsx
 * const rootStore = new RootStore();
 * await rootStore.initialize();
 * rootStore.authStore.loginWithGoogle();
 * ```
 */
export class RootStore {
  /** Application-level state (loading, errors, connection) */
  readonly appStore: AppStore;

  /** Authentication state (user, session, login/logout) */
  readonly authStore: AuthStore;

  /** Character data (primary game state) */
  readonly characterStore: CharacterStore;

  /** User preferences (settings, consent) */
  readonly preferencesStore: PreferencesStore;

  /** DLC entitlements (purchased content) */
  readonly entitlementsStore: EntitlementsStore;

  /** Game state and content packs */
  readonly gameStore: GameStore;

  /** Location and travel management */
  readonly locationStore: LocationStore;

  /** UI state management */
  readonly uiStore: UIStore;

  /** Notification management (toasts and blocking modals) */
  readonly notificationStore: NotificationStore;

  /** Bank item management */
  readonly bankStore: BankStore;

  /** Shop management */
  readonly shopStore: ShopStore;

  /** Skills management (XP, leveling, actions) */
  readonly skillsStore: SkillsStore;

  /** Sync service for server backup */
  readonly syncService: SyncService;

  /** Whether the root store has been initialized */
  private initialized = false;

  constructor() {
    // Initialize stores with reference to root for cross-store access
    this.appStore = new AppStore(this);
    this.authStore = new AuthStore(this);
    this.characterStore = new CharacterStore(this);
    this.preferencesStore = new PreferencesStore(this);
    this.entitlementsStore = new EntitlementsStore(this);
    this.gameStore = new GameStore(this);
    this.locationStore = new LocationStore(this);
    this.uiStore = new UIStore(this);
    this.notificationStore = new NotificationStore(this);
    this.bankStore = new BankStore(this);
    this.shopStore = new ShopStore(this);
    this.skillsStore = new SkillsStore(this);

    // Initialize services
    this.syncService = new SyncService(this);

    // Run localStorage migrations if needed
    LocalStorageService.migrateIfNeeded();
  }

  /**
   * Initialize all stores.
   *
   * Should be called once on app startup.
   * Handles auth check and loads initial data.
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // Initialize auth first (determines if we sync to server)
    // This also proves server is reachable if it succeeds
    await this.authStore.initialize();

    // If auth initialized successfully (any status other than loading),
    // we know the server is connected
    if (this.authStore.status !== 'loading') {
      this.appStore.setConnectionStatus('connected');
      this.appStore.setLoading(false);
    }

    // Initialize other stores (they check auth status internally)
    // Character store loads from localStorage immediately; server sync happens in background
    await Promise.all([
      this.characterStore.initialize(),
      this.preferencesStore.initialize(),
      this.entitlementsStore.initialize(),
    ]);

    // Initialize game store (loads content packs based on entitlements)
    await this.gameStore.initialize();

    // Initialize bank after game store (needs item definitions)
    await this.bankStore.initialize();

    // Start tick loop if there's a restored active character
    // This must happen after gameStore.initialize() so systems are ready
    const activeCharacter = this.characterStore.activeCharacter;
    if (activeCharacter) {
      this.gameStore.startTicking(activeCharacter.id);
    }

    this.initialized = true;
  }

  /**
   * Reset all stores to their initial state.
   *
   * Used for logout to clear all user data.
   */
  reset(): void {
    this.syncService.clearPending();
    this.appStore.reset();
    this.characterStore.reset();
    this.preferencesStore.reset();
    this.entitlementsStore.reset();
    this.gameStore.reset();
    this.locationStore.reset();
    this.uiStore.reset();
    this.notificationStore.reset();
    this.bankStore.reset();
    this.shopStore.reset();
    this.skillsStore.reset();
    // Note: AuthStore.reset() is handled by logout()
  }

  /**
   * Whether the store is initialized.
   */
  get isInitialized(): boolean {
    return this.initialized;
  }
}

/**
 * Singleton instance of the root store.
 * Created once and shared across the application.
 */
export const rootStore = new RootStore();

// Initialize dev helpers in development mode
if (import.meta.env.DEV) {
  void import('@/utils/devHelpers').then(({ initDevHelpers }) => {
    initDevHelpers(rootStore);
  });
}
